import type { ReactNode } from 'react'
import { cn } from './cn'

type BadgeVariant =
  'default' | 'success' | 'warning' | 'danger' | 'error' | 'info' | 'vjal' | 'on-ink' | 'on-ink-accent'
type BadgeTone = 'neutral' | 'ok' | 'success' | 'warn' | 'warning' | 'error' | 'danger' | 'info'

const variants: Record<BadgeVariant, string> = {
  default: 'bg-stone text-ink-secondary',
  success: 'bg-ok-bg text-ok-ink',
  warning: 'bg-warn-bg text-warn-ink',
  danger: 'bg-err-bg text-err-ink',
  error: 'bg-err-bg text-err-ink',
  info: 'bg-info-bg text-info-ink',
  vjal: 'bg-vjal-50 text-vjal-700',
  // v2.1 ON-INK variants — chips on the hero band / navy rail, where light-surface
  // variants wash out (the role-badge class the screen audits caught).
  // `on-ink` = quiet glass chip; `on-ink-accent` = mustard-lit emphasis chip.
  'on-ink': 'bg-veil-strong text-white ring-1 ring-inset ring-veil-line',
  'on-ink-accent': 'bg-mustard/15 text-mustard-300 ring-1 ring-inset ring-mustard/30',
}

const toneVariants: Record<BadgeTone, BadgeVariant> = {
  neutral: 'default',
  ok: 'success',
  success: 'success',
  warn: 'warning',
  warning: 'warning',
  error: 'error',
  danger: 'error',
  info: 'info',
}

export interface BadgeProps {
  /** Legacy visual variant. Use `tone` for status badges. `on-ink` is replaced by `onInk`. */
  variant?: BadgeVariant
  /** Canonical status tone. `danger` is kept as a deprecated alias for `error`. */
  tone?: BadgeTone
  /** Canonical on-ink spelling; maps to the legacy `variant="on-ink"`. */
  onInk?: boolean
  children?: ReactNode
  className?: string
}

export function Badge({ variant = 'default', tone, onInk = false, children, className = '' }: BadgeProps) {
  const resolvedVariant = onInk ? 'on-ink' : tone ? toneVariants[tone] : variant

  return (
    <span
      className={cn(
        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap',
        variants[resolvedVariant],
        className,
      )}
    >
      {children}
    </span>
  )
}
